import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import { gr } from "../tokens";

// Message composer: one field-look row (radius 10, min 44, grows with the text) with
// `start` actions (attach), the input (children: a textarea), and `end` actions (format, send).
// `top` holds what belongs above the text inside the field (formatting bar, image preview).
// The textarea inside is reset to borderless; the whole row gets the focus ring.
// `variant="soft"`: the comment-box look (as on social feeds): tinted, no border, rounded ends.
// `stacked`: the text takes the full width and the `end` actions move to their own line under it, on the
// right (a comment box once the member starts typing, so long text is not squeezed beside the icons).
export default function GrChatComposer({
  start,
  end,
  top,
  children,
  variant = "default",
  stacked = false,
  sx,
}) {
  const soft = variant === "soft";
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-end",
        flexWrap: stacked ? "wrap" : "nowrap",
        gap: gr.space[1],
        minHeight: gr.control.touch,
        p: gr.space[1],
        width: "100%",
        background: soft ? gr.color.tint : gr.color.surface,
        border: `1px solid ${soft ? "transparent" : gr.color.line}`,
        borderRadius: soft ? gr.radius.card : gr.radius.control,
        ...(soft ? { pl: gr.space[2] } : {}),
        fontFamily: gr.font.family,
        transition: "border-color .15s ease, box-shadow .15s ease",
        "&:hover": { borderColor: soft ? gr.color.line : gr.color.lineStrong },
        "&:focus-within": {
          borderColor: gr.color.brand,
          boxShadow: gr.shadow.focus,
        },
        ...sx,
      }}
    >
      {start ? (
        <Box sx={{ display: "flex", alignItems: "center", flex: "none" }}>
          {start}
        </Box>
      ) : null}
      <Box
        sx={{
          flex: stacked ? "1 1 100%" : 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          alignSelf: "center",
        }}
      >
        {top}
        <Box
          sx={{
            "& textarea": {
              display: "block",
              width: "100%",
              resize: "none",
              border: 0,
              outline: 0,
              background: "transparent",
              p: `${gr.space[2]} ${gr.space[2]}`,
              fontFamily: gr.font.family,
              fontSize: gr.font.size.md,
              lineHeight: "20px",
              color: gr.color.ink,
            },
            "& textarea::placeholder": {
              color: gr.color.muted,
              opacity: 1,
              // The hint stays on one line even when the icons leave little room (phones).
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
          }}
        >
          {children}
        </Box>
      </Box>
      {end ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: gr.space[1],
            flex: "none",
            ml: stacked ? "auto" : 0,
          }}
        >
          {end}
        </Box>
      ) : null}
    </Box>
  );
}

GrChatComposer.propTypes = {
  start: PropTypes.node,
  end: PropTypes.node,
  top: PropTypes.node,
  children: PropTypes.node,
  variant: PropTypes.oneOf(["default", "soft"]),
  stacked: PropTypes.bool,
  sx: PropTypes.object,
};
